/**
 * Product Analytics — the in-house, Mixpanel-style admin console for CrediArc.
 * Surfaces the user journey captured by the analytics tracker: active users,
 * sessions, an activity timeline, top events/pages, a conversion funnel, a
 * per-user journey explorer, and per-tenant activity. Platform-admin only.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box, Typography, CircularProgress, Alert, Button,
  ToggleButton, ToggleButtonGroup, FormControlLabel, Switch, Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import GroupIcon from "@mui/icons-material/Group";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import TimerIcon from "@mui/icons-material/Timer";
import HubIcon from "@mui/icons-material/Hub";
import BoltIcon from "@mui/icons-material/Bolt";
import {
  ComposedChart, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from "recharts";
import { isPlatformAdmin } from "../utils/isPlatformAdmin";
import JourneyDrawer from "./JourneyDrawer";
import {
  getAnalyticsOverview, getAnalyticsFunnel, getAnalyticsUsers, getAnalyticsTenants,
} from "../api/tenantApi";

const INK = "#0B2A4A", BLUE = "#1E6FE8", GREEN = "#12A150", MUTED = "#64748b", LINE = "#e3ebf3";

const fmt = (n) => (n == null ? "0" : Number(n).toLocaleString());
const fmtDuration = (ms) => {
  if (!ms || ms < 1000) return "0s";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
};
const fmtRelative = (ms) => {
  if (!ms) return "—";
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};
const shortId = (id) => (id ? String(id).slice(0, 8) : "—");
const prettyEvent = (e) => String(e || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const KpiCard = ({ label, value, sub, icon }) => (
  <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "14px", p: 2.25, background: "#fff", flex: 1, minWidth: 170 }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: MUTED }}>{label}</Typography>
      {icon}
    </Box>
    <Typography sx={{ fontSize: 27, fontWeight: 800, color: INK, lineHeight: 1.1 }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.25 }}>{sub}</Typography>}
  </Box>
);

const Panel = ({ title, sub, right, children, sx }) => (
  <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "14px", background: "#fff", p: 2.25, ...sx }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK }}>{title}</Typography>
        {sub && <Typography sx={{ fontSize: 12.5, color: MUTED }}>{sub}</Typography>}
      </Box>
      {right}
    </Box>
    {children}
  </Box>
);

// A simple horizontal bar list (top events / top pages).
const BarList = ({ items, color, empty }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (!items.length) return <Typography sx={{ fontSize: 13, color: MUTED, py: 2 }}>{empty}</Typography>;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {items.map((i) => (
        <Box key={i.key}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
            <Typography sx={{ fontSize: 12.5, color: INK, fontWeight: 600, maxWidth: "78%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {i.label || i.key}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: MUTED, fontWeight: 700 }}>{fmt(i.count)}</Typography>
          </Box>
          <Box sx={{ height: 7, borderRadius: 4, background: "#eef2f7" }}>
            <Box sx={{ height: 7, borderRadius: 4, width: `${(i.count / max) * 100}%`, background: color }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const RANGES = [{ label: "7d", days: 7 }, { label: "30d", days: 30 }, { label: "90d", days: 90 }];
const FILTER_DEBOUNCE_MS = 300;

export default function ProductAnalytics({ embedded = false }) {
  const isAdmin = useSelector((s) => s.userInfo.isAdmin);
  const email = useSelector((s) => s.userInfo.email);
  const platformAdmin = isPlatformAdmin(email, isAdmin);

  const [days, setDays] = useState(30);
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const loadRequest = useRef(0);

  const [journeyUser, setJourneyUser] = useState(null);

  const load = useCallback(async () => {
    if (!platformAdmin) return;
    const requestId = ++loadRequest.current;
    setLoading(true);
    setError(null);
    const params = { days, includeAdmins };
    try {
      const [ov, fn, us, tn] = await Promise.all([
        getAnalyticsOverview(params).catch(() => null),
        getAnalyticsFunnel(params).catch(() => null),
        getAnalyticsUsers(params).catch(() => []),
        getAnalyticsTenants(params).catch(() => []),
      ]);
      if (requestId !== loadRequest.current) return;
      setOverview(ov);
      setFunnel(fn);
      setUsers(Array.isArray(us) ? us : []);
      setTenants(Array.isArray(tn) ? tn : []);
      if (!ov) setError("Couldn't load analytics. If this is the first time, data appears as users start using the app.");
    } catch (e) {
      if (requestId !== loadRequest.current) return;
      setError(e?.message || "Failed to load analytics");
    } finally {
      if (requestId === loadRequest.current) setLoading(false);
    }
  }, [platformAdmin, days, includeAdmins]);

  useEffect(() => {
    const timer = setTimeout(load, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [load]);

  const openJourney = (u) => setJourneyUser(u);

  const totals = overview?.totals || {};
  const timeline = useMemo(() => (overview?.timeline || []).map((d) => ({
    day: d.day?.slice(5), // MM-DD
    activeUsers: d.activeUsers,
    events: d.events,
  })), [overview]);

  if (!platformAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">Product Analytics is available to CrediArc platform admins only.</Alert>
      </Box>
    );
  }

  return (
    <Box data-track-area="Product analytics workspace" sx={{
      p: { xs: 2, md: 3 },
      background: embedded ? "#fff" : "#f7f9fc",
      minHeight: embedded ? "auto" : "100vh",
    }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK }}>Product Analytics</Typography>
          <Typography sx={{ fontSize: 13.5, color: MUTED }}>
            How users move through the platform — track and optimize the journey.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <FormControlLabel
            control={<Switch size="small" checked={includeAdmins} onChange={(e) => setIncludeAdmins(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12.5, color: MUTED }}>Include internal</Typography>}
            sx={{ mr: 0 }}
          />
          <ToggleButtonGroup
            size="small" exclusive value={days}
            onChange={(_, v) => v && setDays(v)}
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.5, py: 0.4, color: MUTED, borderColor: LINE },
                  "& .Mui-selected": { color: `${INK} !important`, background: "#eaf1fe !important" } }}
          >
            {RANGES.map((r) => <ToggleButton key={r.days} value={r.days}>{r.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <Button onClick={load} startIcon={<RefreshIcon />} size="small"
            sx={{ textTransform: "none", color: BLUE, fontWeight: 700 }}>Refresh</Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>
      ) : (
        <>
          {error && <Alert severity="info" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

          {/* KPIs */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2.5 }}>
            <KpiCard label="Active users" value={fmt(totals.activeUsers)} sub={`in the last ${days} days`} icon={<GroupIcon sx={{ color: BLUE, fontSize: 20 }} />} />
            <KpiCard label="Sessions" value={fmt(totals.sessions)} sub="distinct visits" icon={<BoltIcon sx={{ color: "#7A5AF8", fontSize: 20 }} />} />
            <KpiCard label="Avg session" value={fmtDuration(totals.avgSessionMs)} sub="time in app" icon={<TimerIcon sx={{ color: "#E7A13C", fontSize: 20 }} />} />
            <KpiCard label="Events" value={fmt(totals.events)} sub="actions captured" icon={<TouchAppIcon sx={{ color: GREEN, fontSize: 20 }} />} />
            <KpiCard label="Active tenants" value={fmt(totals.activeTenants)} sub="with activity" icon={<HubIcon sx={{ color: INK, fontSize: 20 }} />} />
          </Box>

          {/* Activity timeline */}
          <Panel title="Activity over time" sub="Active users (area) and events (bars) per day" sx={{ mb: 2.5 }}>
            {timeline.length ? (
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={timeline} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <defs>
                      <linearGradient id="auFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={BLUE} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={BLUE} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <RTooltip contentStyle={{ borderRadius: 10, border: `1px solid ${LINE}`, fontSize: 12 }} />
                    <Bar yAxisId="right" dataKey="events" fill="#dbe7fb" radius={[3, 3, 0, 0]} name="Events" />
                    <Area yAxisId="left" type="monotone" dataKey="activeUsers" stroke={BLUE} strokeWidth={2.2} fill="url(#auFill)" name="Active users" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            ) : <Typography sx={{ fontSize: 13, color: MUTED, py: 3 }}>No activity in this window yet.</Typography>}
          </Panel>

          {/* Funnel + Top events + Top pages */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.2fr 1fr 1fr" }, gap: 2.5, mb: 2.5 }}>
            <Panel title="Conversion funnel" sub="Share of users reaching each step">
              {funnel?.steps?.length ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {funnel.steps.map((s, i) => (
                    <Box key={i}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                        <Typography sx={{ fontSize: 12.5, color: INK, fontWeight: 700 }}>
                          {i + 1}. {s.label}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                          {fmt(s.users)} · {s.pctOfStart}%
                          {i > 0 && <span style={{ color: s.pctOfPrev < 60 ? "#E5484D" : MUTED }}> ({s.pctOfPrev}%)</span>}
                        </Typography>
                      </Box>
                      <Box sx={{ height: 22, borderRadius: 6, background: "#eef2f7", overflow: "hidden" }}>
                        <Box sx={{ height: 22, width: `${Math.max(s.pctOfStart, 2)}%`, background: `linear-gradient(90deg, ${BLUE}, #4f93f5)`, transition: "width .4s" }} />
                      </Box>
                    </Box>
                  ))}
                  <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.5 }}>
                    Based on {fmt(funnel.totalUsers)} users who started. Steps are default; ask to customize.
                  </Typography>
                </Box>
              ) : <Typography sx={{ fontSize: 13, color: MUTED, py: 2 }}>Not enough data for a funnel yet.</Typography>}
            </Panel>

            <Panel title="Top events" sub="Most frequent actions">
              <BarList items={(overview?.topEvents || []).map((e) => ({ ...e, label: prettyEvent(e.key) }))} color={GREEN} empty="No events yet." />
            </Panel>

            <Panel title="Top pages" sub="Most viewed screens">
              <BarList items={overview?.topPages || []} color={BLUE} empty="No page views yet." />
            </Panel>
          </Box>

          {/* Users + Tenants */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.6fr 1fr" }, gap: 2.5 }}>
            <Panel title="Users" sub="Click a user to see their full journey">
              <Box sx={{ overflowX: "auto" }}>
                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th": { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: MUTED, fontWeight: 700, py: 0.75, borderBottom: `1px solid ${LINE}` }, "& td": { fontSize: 13, color: INK, py: 1, borderBottom: `1px solid ${LINE}` } }}>
                  <thead>
                    <tr><th>User</th><th>Tenant</th><th>Sessions</th><th>Events</th><th>Last seen</th></tr>
                  </thead>
                  <tbody>
                    {users.length ? users.slice(0, 100).map((u) => (
                      <Box component="tr" key={u.userId} onClick={() => openJourney(u)}
                        sx={{ cursor: "pointer", "&:hover td": { background: "#f4f8ff" } }}>
                        <td>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 26, height: 26, borderRadius: "50%", background: "#eaf1fe", color: BLUE, fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {(u.email || u.userId || "?").slice(0, 1).toUpperCase()}
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK }}>{u.email || shortId(u.userId)}</Typography>
                              {u.isAdmin && <Chip label="internal" size="small" sx={{ height: 16, fontSize: 10, background: "#fff4e5", color: "#B76E00" }} />}
                            </Box>
                          </Box>
                        </td>
                        <td><Typography sx={{ fontSize: 12.5, color: MUTED, fontFamily: "monospace" }}>{shortId(u.tenantId)}</Typography></td>
                        <td>{fmt(u.sessions)}</td>
                        <td>{fmt(u.events)}</td>
                        <td><Typography sx={{ fontSize: 12.5, color: MUTED }}>{fmtRelative(u.lastSeen)}</Typography></td>
                      </Box>
                    )) : <tr><td colSpan={5}><Typography sx={{ fontSize: 13, color: MUTED, py: 2 }}>No users in this window yet.</Typography></td></tr>}
                  </tbody>
                </Box>
              </Box>
            </Panel>

            <Panel title="Tenants" sub="Activity by account">
              <Box sx={{ overflowX: "auto" }}>
                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th": { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: MUTED, fontWeight: 700, py: 0.75, borderBottom: `1px solid ${LINE}` }, "& td": { fontSize: 13, color: INK, py: 1, borderBottom: `1px solid ${LINE}` } }}>
                  <thead>
                    <tr><th>Tenant</th><th>Users</th><th>Events</th><th>Last seen</th></tr>
                  </thead>
                  <tbody>
                    {tenants.length ? tenants.slice(0, 100).map((t) => (
                      <tr key={t.tenantId}>
                        <td><Typography sx={{ fontSize: 12.5, color: INK, fontFamily: "monospace" }}>{shortId(t.tenantId)}</Typography></td>
                        <td>{fmt(t.users)}</td>
                        <td>{fmt(t.events)}</td>
                        <td><Typography sx={{ fontSize: 12.5, color: MUTED }}>{fmtRelative(t.lastSeen)}</Typography></td>
                      </tr>
                    )) : <tr><td colSpan={4}><Typography sx={{ fontSize: 13, color: MUTED, py: 2 }}>No tenant activity yet.</Typography></td></tr>}
                  </tbody>
                </Box>
              </Box>
            </Panel>
          </Box>
        </>
      )}

      {/* Journey drawer (shared component) */}
      <JourneyDrawer open={!!journeyUser} user={journeyUser} onClose={() => setJourneyUser(null)} />
    </Box>
  );
}
